// SplatView.tsx — React host for the WebGL2 splat renderer with orbit/touch
// controls, shared by Capture's live preview and the Viewer. Draws on demand
// (camera moved, cloud or filter changed, or auto-rotate on), frames the
// cloud on first load, and overlays measurement annotations — picked points
// and the segment between them, plus the camera trajectory with the active
// camera's frustum — on a 2D canvas above the GL one. A tap on a trajectory
// dot selects that camera (onPathPick); other taps are turned into
// world-space gaussian picks for the measure tool. Boxes (guided
// segmentation's 3D box tool) draw as wireframes, the editable one with its
// handles; onGrab lets the host claim a press (a handle, or drawing a new
// box) before it orbits.
//
// GL failures are shown, not swallowed: a renderer that cannot be created
// (no WebGL2, shader/link failure) and a lost GPU context (driver reset, too
// many contexts, a backgrounded tab on mobile) both replace the view with the
// reason and a Reload button. A lost context is also recovered on its own:
// the lost event is preventDefault()-ed so the browser may restore it, and on
// webglcontextrestored the renderer is re-created on the same canvas and the
// current cloud and label filter re-uploaded.

import { useEffect, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import { BsArrowClockwise } from 'react-icons/bs';
import { cloudBounds } from '../splat/format';
import type { SplatCloud } from '../splat/format';
import { mat4Multiply, projectToScreen } from '../splat/math';
import type { Mat4, Vec3 } from '../splat/math';
import { BOX_EDGES, BOX_HANDLES, boxCorners, handlePoint } from '../splat/boxGizmo';
import type { Box3 } from '../splat/boxGizmo';
import { OrbitControls, fitState } from '../splat/orbit';
import type { GrabHandler } from '../splat/orbit';
import { nearestScreenPoint, pickGaussian, rayFromScreen } from '../splat/pick';
import type { PickHit } from '../splat/pick';
import { focalFromProjection } from '../splat/project';
import { SplatRenderer } from '../splat/renderer';

// SplatPath is a camera trajectory to overlay: the camera centres in capture
// order, the highlighted one, and that camera's image-corner frustum (TL, TR,
// BR, BL in world space).
export interface SplatPath {
  centers: readonly Vec3[];
  active?: number;
  frustum?: readonly Vec3[] | null;
}

const PATH_PICK_RADIUS_PX = 14;

// CONTEXT_LOST_MESSAGE is shown while the browser has taken the GPU context
// away; it clears by itself if the context is restored.
const CONTEXT_LOST_MESSAGE = 'The 3D view lost its GPU context. It will come back on its own if the browser restores it; otherwise reload the page.';
// OverlayBox is a world box to draw; `handles` marks the editable one.
export interface OverlayBox extends Box3 {
  color: string;
  handles?: boolean;
}

// ViewCamera is the camera at the moment of a grab, in CSS px.
export interface ViewCamera {
  view: Mat4;
  proj: Mat4;
  width: number;
  height: number;
}

export interface SplatViewProps {
  cloud: SplatCloud | null;
  labels?: readonly number[] | null; // null/empty = all
  height?: string; // CSS height, e.g. '60vh'
  autoRotate?: boolean;
  fitKey?: string | number; // change to re-frame the camera
  fitPoints?: readonly Vec3[]; // extra world points the framing must include (e.g. camera centres)
  points?: readonly Vec3[]; // annotation points (world, m)
  onPick?: (hit: PickHit | null) => void;
  path?: SplatPath | null;
  onPathPick?: (i: number) => void; // i indexes path.centers
  boxes?: readonly OverlayBox[];
  onGrab?: (x: number, y: number, cam: ViewCamera) => GrabHandler | null;
  emptyText?: string;
}

export function SplatView({ cloud, labels, height = '60vh', autoRotate, fitKey, points, onPick, path, onPathPick, fitPoints, boxes, onGrab, emptyText }: SplatViewProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const glRef = useRef<HTMLCanvasElement>(null);
  const annRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<SplatRenderer | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const dirtyRef = useRef(true);
  const fittedRef = useRef<string | number | undefined>(undefined);
  const propsRef = useRef({ cloud, labels, autoRotate, points, onPick, path, onPathPick, fitPoints, boxes, onGrab });
  propsRef.current = { cloud, labels, autoRotate, points, onPick, path, onPathPick, fitPoints, boxes, onGrab };
  const [glError, setGlError] = useState('');

  // One-time GL + controls setup and the render loop.
  useEffect(() => {
    const wrap = wrapRef.current, glc = glRef.current, ann = annRef.current;
    if (!wrap || !glc || !ann) return;
    // makeRenderer creates a renderer on the GL canvas, or shows why not.
    const makeRenderer = (why: string): SplatRenderer | null => {
      try {
        const r = new SplatRenderer(glc);
        console.info('[openscan/SplatView] renderer created', { why, w: glc.width, h: glc.height });
        return r;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[openscan/SplatView] renderer creation failed', { why, msg });
        setGlError(msg);
        return null;
      }
    };
    const first = makeRenderer('mount');
    if (!first) return;
    rendererRef.current = first;
    const controls = new OrbitControls(fitState([-0.1, -0.1, 0], [0.1, 0.1, 0.1]));
    controlsRef.current = controls;
    controls.onChange = () => {
      dirtyRef.current = true;
    };
    controls.onTap = (t) => {
      const w0 = wrap.clientWidth, h0 = wrap.clientHeight;
      const { path: tapPath, onPathPick: pathCb } = propsRef.current;
      if (pathCb && tapPath && tapPath.centers.length > 0) {
        const vp = mat4Multiply(controls.proj(w0 / h0), controls.view());
        const screen = tapPath.centers.map((c) => projectToScreen(vp, c, w0, h0));
        const i = nearestScreenPoint(screen, t.x, t.y, PATH_PICK_RADIUS_PX);
        if (i >= 0) {
          console.debug('[openscan/SplatView] tap picked trajectory camera', { i, x: t.x, y: t.y });
          pathCb(i);
          return;
        }
      }
      const cb = propsRef.current.onPick;
      const c = rendererRef.current?.currentCloud;
      if (!cb || !c) return;
      const w = wrap.clientWidth, h = wrap.clientHeight;
      const ray = rayFromScreen(controls.view(), controls.proj(w / h), t.x, t.y, w, h);
      const hit = ray
        ? pickGaussian(c, ray, { labels: propsRef.current.labels ?? [], fovy: controls.state.fovy, viewportHeightPx: h })
        : null;
      console.debug('[openscan/SplatView] tap pick', { x: t.x, y: t.y, id: hit?.id ?? null });
      cb(hit);
    };
    controls.onGrab = (t) => {
      const cb = propsRef.current.onGrab;
      if (!cb) return null;
      const width = wrap.clientWidth, height = wrap.clientHeight;
      return cb(t.x, t.y, { view: controls.view(), proj: controls.proj(width / height), width, height });
    };
    controls.attach(wrap);

    const resize = (): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(wrap.clientWidth * dpr));
      const h = Math.max(1, Math.round(wrap.clientHeight * dpr));
      for (const c of [glc, ann]) {
        if (c.width !== w || c.height !== h) {
          c.width = w;
          c.height = h;
        }
      }
      dirtyRef.current = true;
    };
    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    resize();

    let raf = 0;
    let last = performance.now();
    const frame = (now: number): void => {
      const renderer = rendererRef.current;
      if (!renderer) {
        raf = 0; // context lost: the loop restarts on restore
        return;
      }
      raf = requestAnimationFrame(frame);
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (propsRef.current.autoRotate) controls.set({ azimuth: controls.state.azimuth + dt * 0.4 });
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      const w = glc.width, h = glc.height;
      const view = controls.view();
      const proj = controls.proj(w / h);
      const [fx, fy] = focalFromProjection(proj, w, h);
      renderer.render({ view, proj, fx, fy });
      drawAnnotations(ann, mat4Multiply(proj, view), propsRef.current.points ?? [], propsRef.current.path ?? null, propsRef.current.boxes ?? []);
    };
    raf = requestAnimationFrame(frame);

    // Context loss: stop drawing and say so. preventDefault() is what tells
    // the browser we can handle a restore.
    const onContextLost = (e: Event): void => {
      e.preventDefault();
      cancelAnimationFrame(raf);
      raf = 0;
      // The lost renderer's GL objects died with the context; nothing to free.
      rendererRef.current = null;
      console.warn('[openscan/SplatView] WebGL context lost; waiting for the browser to restore it');
      setGlError(CONTEXT_LOST_MESSAGE);
    };
    // Context restored: rebuild the renderer and re-upload what it showed.
    const onContextRestored = (): void => {
      const r = makeRenderer('context restored');
      if (!r) return;
      const { cloud: c, labels: l } = propsRef.current;
      r.setCloud(c ?? { ...emptyPlaceholder });
      r.setLabelFilter(l && l.length ? [...l] : null);
      rendererRef.current = r;
      dirtyRef.current = true;
      setGlError('');
      console.info('[openscan/SplatView] WebGL context restored', { splats: c?.count ?? 0, labels: l?.length ?? 0 });
      if (!raf) {
        last = performance.now();
        raf = requestAnimationFrame(frame);
      }
    };
    glc.addEventListener('webglcontextlost', onContextLost);
    glc.addEventListener('webglcontextrestored', onContextRestored);

    return () => {
      cancelAnimationFrame(raf);
      glc.removeEventListener('webglcontextlost', onContextLost);
      glc.removeEventListener('webglcontextrestored', onContextRestored);
      ro.disconnect();
      controls.detach();
      rendererRef.current?.dispose();
      rendererRef.current = null;
      controlsRef.current = null;
    };
  }, []);

  // Cloud swaps re-upload; the first non-empty cloud (or a new fitKey) frames it.
  useEffect(() => {
    const r = rendererRef.current, c = controlsRef.current;
    if (!r || !c) return;
    r.setCloud(cloud ?? { ...emptyPlaceholder });
    dirtyRef.current = true;
    const key = fitKey ?? 'default';
    if (cloud && cloud.count > 0 && fittedRef.current !== key) {
      const l = propsRef.current.labels;
      const fitLabels = l && l.length ? l : [];
      const b = cloudBounds(cloud, fitLabels, 0.02) ?? cloudBounds(cloud, [], 0.02);
      if (b) {
        for (const p of propsRef.current.fitPoints ?? []) {
          for (let k = 0; k < 3; k++) {
            b.min[k] = Math.min(b.min[k], p[k]);
            b.max[k] = Math.max(b.max[k], p[k]);
          }
        }
        c.state = { ...fitState(b.min, b.max), fovy: c.state.fovy };
        fittedRef.current = key;
        console.debug('[openscan/SplatView] framed cloud', b);
      }
    }
  }, [cloud, fitKey]);

  // Keyed by content, so a parent passing a fresh array each render is free.
  const labelKey = (labels ?? []).join(',');
  useEffect(() => {
    rendererRef.current?.setLabelFilter(labelKey ? labelKey.split(',').map(Number) : null);
    dirtyRef.current = true;
  }, [labelKey, cloud]);

  useEffect(() => {
    dirtyRef.current = true;
  }, [points, path, boxes]);

  const empty = !cloud || cloud.count === 0;
  return (
    <div ref={wrapRef} className="splat-view" style={{ height }}>
      <canvas ref={glRef} />
      <canvas ref={annRef} className="splat-annotations" />
      {glError ? (
        <div className="splat-view-empty splat-view-error" role="alert">
          <span>{glError}</span>
          <Button size="sm" variant="outline-secondary" onClick={reloadPage}>
            <BsArrowClockwise /> Reload page
          </Button>
        </div>
      ) : (
        empty && <div className="splat-view-empty">{emptyText || 'No splats yet'}</div>
      )}
    </div>
  );
}

// reloadPage is the error overlay's way out when the GPU will not come back.
function reloadPage(): void {
  console.info('[openscan/SplatView] reloading the page after a GL failure');
  window.location.reload();
}

const emptyPlaceholder: SplatCloud = {
  count: 0,
  ids: new Uint32Array(0),
  pos: new Float32Array(0),
  logScale: new Float32Array(0),
  rot: new Float32Array(0),
  color: new Float32Array(0),
  opacity: new Float32Array(0),
  label: new Uint32Array(0),
};

function drawAnnotations(canvas: HTMLCanvasElement, viewProj: Float32Array, points: readonly Vec3[], path: SplatPath | null, boxes: readonly OverlayBox[]): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  if (path && path.centers.length > 0) drawPath(ctx, canvas, viewProj, path, dpr);
  for (const b of boxes) drawBox(ctx, canvas, viewProj, b, dpr);
  if (points.length === 0) return;
  const px = points.map((p) => projectToScreen(viewProj, p, canvas.width, canvas.height));
  ctx.lineWidth = 2 * dpr;
  ctx.strokeStyle = '#14b8a6';
  if (px.length >= 2 && px[0] && px[1]) {
    ctx.setLineDash([6 * dpr, 4 * dpr]);
    ctx.beginPath();
    ctx.moveTo(px[0][0], px[0][1]);
    ctx.lineTo(px[1][0], px[1][1]);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  for (const p of px) {
    if (!p) continue;
    ctx.beginPath();
    ctx.arc(p[0], p[1], 6 * dpr, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(20, 184, 166, 0.35)';
    ctx.fill();
    ctx.stroke();
  }
}

const PATH_COLOR = '#f59e0b';
const PATH_ACTIVE_COLOR = '#fde047';

// drawPath strokes the camera centres in capture order (breaking the line at
// any centre behind the view), a dot per camera, and the active camera as a
// larger dot with its frustum: rays to the four image corners plus the
// image rectangle.
function drawPath(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, viewProj: Float32Array, path: SplatPath, dpr: number): void {
  const px = path.centers.map((c) => projectToScreen(viewProj, c, canvas.width, canvas.height));
  ctx.save();
  ctx.lineWidth = 1.5 * dpr;
  ctx.strokeStyle = PATH_COLOR;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  let open = false;
  for (const p of px) {
    if (!p) {
      open = false;
      continue;
    }
    if (open) ctx.lineTo(p[0], p[1]);
    else ctx.moveTo(p[0], p[1]);
    open = true;
  }
  ctx.stroke();
  ctx.fillStyle = PATH_COLOR;
  for (const p of px) {
    if (!p) continue;
    ctx.beginPath();
    ctx.arc(p[0], p[1], 2.5 * dpr, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  const a = path.active;
  const ap = a != null ? px[a] : null;
  if (ap) {
    const corners = (path.frustum ?? []).map((c) => projectToScreen(viewProj, c, canvas.width, canvas.height));
    ctx.strokeStyle = PATH_ACTIVE_COLOR;
    ctx.lineWidth = 1.5 * dpr;
    if (corners.length === 4 && corners.every((c) => c)) {
      ctx.beginPath();
      for (const c of corners) {
        ctx.moveTo(ap[0], ap[1]);
        ctx.lineTo(c![0], c![1]);
      }
      ctx.moveTo(corners[0]![0], corners[0]![1]);
      for (const c of [...corners.slice(1), corners[0]]) ctx.lineTo(c![0], c![1]);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(ap[0], ap[1], 5 * dpr, 0, Math.PI * 2);
    ctx.fillStyle = PATH_ACTIVE_COLOR;
    ctx.fill();
  }
  ctx.restore();
}

// drawBox strokes the 12 edges (skipping any with an end behind the camera);
// the editable box is solid with a dot per face handle and a square move
// handle, the others dashed and fainter.
function drawBox(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, viewProj: Float32Array, b: OverlayBox, dpr: number): void {
  const px = boxCorners(b).map((c) => projectToScreen(viewProj, c, canvas.width, canvas.height));
  ctx.save();
  ctx.strokeStyle = b.color;
  ctx.lineWidth = (b.handles ? 2 : 1.5) * dpr;
  ctx.globalAlpha = b.handles ? 0.95 : 0.6;
  if (!b.handles) ctx.setLineDash([5 * dpr, 4 * dpr]);
  ctx.beginPath();
  for (const [i, j] of BOX_EDGES) {
    const p = px[i], q = px[j];
    if (!p || !q) continue;
    ctx.moveTo(p[0], p[1]);
    ctx.lineTo(q[0], q[1]);
  }
  ctx.stroke();
  ctx.setLineDash([]);
  if (b.handles) {
    ctx.globalAlpha = 1;
    ctx.lineWidth = 1.5 * dpr;
    ctx.strokeStyle = '#ffffff';
    ctx.fillStyle = b.color;
    for (const h of BOX_HANDLES) {
      const p = projectToScreen(viewProj, handlePoint(b, h), canvas.width, canvas.height);
      if (!p) continue;
      ctx.beginPath();
      if (h.kind === 'move') ctx.rect(p[0] - 6 * dpr, p[1] - 6 * dpr, 12 * dpr, 12 * dpr);
      else ctx.arc(p[0], p[1], 6 * dpr, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  }
  ctx.restore();
}
